import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { loadScript } from "../pages/payment Test/loadscript";
import { toast } from "react-toastify";
import { paymentEndpoint } from "../services/api";

export const useRazorpayPayment = () => {
  const [loading, setLoading] = useState(false);
  // `verifying` is true only during the backend verify+share step (after Razorpay confirms)
  const [verifying, setVerifying] = useState(false);
  const navigate = useNavigate();
  const { user, isLoggedIn } = useAuth();

  const initiatePayment = async ({
    amount,
    unitId = [],
    subjectId = [],
    branchId = [],
  }) => {
    if (!isLoggedIn) {
      toast.error("Please login to purchase notes");
      navigate("/login");
      return;
    }

    try {
      setLoading(true);

      // 1. Load Razorpay SDK
      const scriptLoaded = await loadScript(
        "https://checkout.razorpay.com/v1/checkout.js"
      );
      if (!scriptLoaded) {
        toast.error("Failed to load payment gateway. Please try again.");
        setLoading(false);
        return;
      }

      // 2. Create Order
      const orderResponse = await fetch(paymentEndpoint.CREATE_ORDER, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ amount, unitId, subjectId, branchId }),
      });

      const orderData = await orderResponse.json();
      if (!orderData.success) {
        // Handle duplicate purchase error with concise message
        if (orderData.duplicates) {
          const {
            units = [],
            subjects = [],
            branches = [],
          } = orderData.duplicates;
          const totalDuplicates =
            units.length + subjects.length + branches.length;

          toast.warning(
            `You already own ${totalDuplicates} of these item(s). Check your Profile or select different items.`,
            { autoClose: 4000 }
          );
          setLoading(false);
          return;
        }
        throw new Error(orderData.message || "Failed to create order");
      }

      const { paymentRecord, key } = orderData;

      // 3. Razorpay Options
      const options = {
        key: key,
        amount: paymentRecord.amount * 100,
        currency: paymentRecord.currency,
        name: "ReferMe",
        description: "Educational Content Purchase",
        order_id: paymentRecord.orderId,
        handler: async function (response) {
          // Razorpay confirmed payment — show "do not close" overlay immediately
          setVerifying(true);

          try {
            // 4. Verify Payment & share Drive files (backend handles both)
            const verifyResponse = await fetch(paymentEndpoint.VERIFY_PAYMENT, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({
                  orderId: paymentRecord.orderId,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                }),
              }
            );

            const verifyData = await verifyResponse.json();
            if (!verifyData.success) {
              throw new Error(
                verifyData.message || "Payment verification failed"
              );
            }

            // 5. Success - Files are automatically shared by backend
            toast.success("Payment successful! Access granted to your email.");

            // Keep overlay visible for 1.5 s then redirect to profile
            setTimeout(() => {
              setVerifying(false);
              setLoading(false);
              navigate("/profile");
            }, 1500);
          } catch (error) {
            console.error("Payment verification error:", error);
            toast.error("Payment verification failed: " + error.message);
            setVerifying(false);
            setLoading(false);
          }
        },
        prefill: {
          name: user?.firstname || user?.email || "",
          email: user?.email || "",
          contact: user?.phone || "",
        },
        theme: {
          color: "#7c3aed",
        },
        modal: {
          ondismiss: function () {
            toast.info("Payment cancelled");
            setLoading(false);
          },
        },
      };

      // 6. Open Razorpay Checkout
      setLoading(false);
      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      console.error("Payment initiation error:", error);
      toast.error("Payment failed: " + error.message);
      setLoading(false);
    }
  };

  return { initiatePayment, loading, verifying };
};
